// Assets
import "./assets/css/bootstrap.min.css";
import "./assets/vendors/css/vendors.min.css";
import "./assets/vendors/css/daterangepicker.min.css";
import "./assets/css/theme.min.css";

// Components
import Layout from "./components/base/Layout";

// Pages
import Dashboard from "./pages/dashboard/Dashboard";
import UnitMeasure from "./pages/inventory/UnitMeasure";
import UserProducts from "./pages/inventory/UserProducts";
import Login from "./pages/authentication/Login";
import ChangePassword from "./pages/authentication/ChangePassword";
import ProfileCreate from "./pages/profile/ProfileCreate";
import ProfileUsers from './pages/profile/ProfileUsers';
import Notifications from "./pages/notification/Notifications";
import InvoiceMethods from "./pages/invoice/InvoiceMethods";
import ConfirmChangePassword from "./pages/authentication/ConfirmChangePassword";
import ForgetPassword from "./pages/authentication/ForgetPassword";
import Country from "./pages/location/Country";

import Province from "./pages/location/Province";
import City from "./pages/location/City";
import InvoicePatterns from "./pages/invoice/InvoicePatterns";
import InvoiceTypes from "./pages/invoice/InvoiceTypes";
import InvoiceSender from "./pages/invoice/InvoiceSender";
import Invoices from "./pages/invoice/Invoices";
import Rule from "./pages/terms-and-rules/Rule";
import InvoiceFields from "./pages/invoice/InvoiceFields";
import TicketCreate from "./pages/ticket/TicketCreate";
import TicketList from "./pages/ticket/TicketList";
import TicketChat from "./pages/ticket/TicketChat";
import ProfileDetails from "./pages/profile/ProfileDetails";
import SubscriptionPlans from "./pages/dashboard/SubscriptionPlans";
import SubscriptionHistory from "./pages/dashboard/SubscriptionHistory";
import CancellationInvoices from "./pages/invoice/CancellationInvoice";
import ReturnedInvoices from "./pages/invoice/ReturnedInvoice";
import CorrectionInvoice from "./pages/invoice/CorrectionInvoice";
import PurchaseForm from "./pages/purchase/PurchaseForm";
import SaleForm from "./pages/sale/SaleForm";
import NotFound from "./pages/authentication/NotFound";
import IndexPage from "./pages/index/Index";
import AboutUsPage from "./pages/index/AboutUsPage";

// Routes
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import routeConfig from "./routes/Config";
import { useEffect } from "react";

const AuthChecker = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const checkAuth = () => {
        const publicPaths = ['/login/', '/register/', '/change-password/', '/confirm-change-password/', '/forget-password/', '/', '/about-us/'];
        if (publicPaths.some(path => location.pathname.startsWith(path))) return;

        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/login/');
            return;
        }

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Date.now() / 1000;
            if (payload.exp && payload.exp < now) {
                localStorage.removeItem('access_token');
                navigate('/login/');
            }
        } catch (e) {
            console.error("Invalid token format");
            localStorage.removeItem('access_token');
            navigate('/login/');
        }
    };

    useEffect(() => {
        checkAuth();
        const interval = setInterval(checkAuth, 60000);
        return () => clearInterval(interval);
    }, [location.pathname]);

    return null;
};

const PageTitle = ({ title, children }) => {
    useEffect(() => {
        document.title = title || 'فارسین';

        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = '/assets/images/redesigned.png';
    }, [title]);
    return children;
};

const elementMap = {
    Dashboard: <Dashboard />,
    UnitMeasure: <UnitMeasure />,
    UserProducts: <UserProducts />,
    ProfileCreate: <ProfileCreate />,
    ProfileUsers: <ProfileUsers />,
    ProfileDetails: <ProfileDetails />,
    Notifications: <Notifications />,
    InvoiceMethods: <InvoiceMethods />,
    Country: <Country />,
    Province: <Province />,
    City: <City />,
    InvoicePatterns: <InvoicePatterns />,
    InvoiceTypes: <InvoiceTypes />,
    InvoiceSender: <InvoiceSender />,
    Invoices: <Invoices />,
    Rule: <Rule />,
    InvoiceFields: <InvoiceFields />,
    TicketList: <TicketList />,
    TicketCreate: <TicketCreate />,
    TicketChat: <TicketChat />,
    SubscriptionPlans: <SubscriptionPlans />,
    SubscriptionHistory: <SubscriptionHistory />,
    CancellationInvoices: <CancellationInvoices />, 
    ReturnedInvoices: <ReturnedInvoices />, 
    CorrectionInvoice: <CorrectionInvoice />, 
    PurchaseForm: <PurchaseForm />,
    SaleForm: <SaleForm />,
    Index: <IndexPage />,
    AboutUsPage: <AboutUsPage />,
};

function App() {
    return (
        <BrowserRouter>
            <AuthChecker />
            <Routes>
                <Route element={<Layout />}>
                    {routeConfig.map(route => (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={
                                <PageTitle title={route.meta.title}>
                                    {elementMap[route.elementKey] || <div />}
                                </PageTitle>
                            }
                        />
                    ))}
                    <Route path="/invoice/cancellation/:id/" element={<PageTitle title="ابطال صورتحساب"><CancellationInvoices mode='create'></CancellationInvoices></PageTitle>}></Route>
                    <Route path="/invoice/cancellation/:id/" element={<PageTitle title="ابطال صورتحساب"><CancellationInvoices mode='edit'></CancellationInvoices></PageTitle>}></Route>
                    <Route path="/invoice/cancellation/:id/" element={<PageTitle title="ابطال صورتحساب"><CancellationInvoices mode='view'></CancellationInvoices></PageTitle>}></Route>
                    <Route path="/invoice/returned/:id/" element={<PageTitle title="برگشت از فروش صورتحساب"><ReturnedInvoices mode='create'></ReturnedInvoices></PageTitle>}></Route>
                    <Route path="/invoice/returned/:id/" element={<PageTitle title="برگشت از فروش صورتحساب"><ReturnedInvoices mode='edit'></ReturnedInvoices></PageTitle>}></Route>
                    <Route path="/invoice/returned/:id/" element={<PageTitle title="برگشت از فروش صورتحساب"><ReturnedInvoices mode='view'></ReturnedInvoices></PageTitle>}></Route>
                    <Route path="/invoice/correction/:id/" element={<PageTitle title="اصلاح صورتحساب"><CorrectionInvoice mode='create'></CorrectionInvoice></PageTitle>}></Route>
                    <Route path="/invoice/correction/:id/" element={<PageTitle title="اصلاح صورتحساب"><CorrectionInvoice mode='edit'></CorrectionInvoice></PageTitle>}></Route>
                    <Route path="/invoice/correction/:id/" element={<PageTitle title="اصلاح صورتحساب"><CorrectionInvoice mode='view'></CorrectionInvoice></PageTitle>}></Route>
                </Route>

                <Route path="/login/" element={<PageTitle title="ورود"><Login /></PageTitle>} />
                <Route path="/change-password/" element={<PageTitle title="تغییر رمز عبور"><ChangePassword /></PageTitle>} />
                <Route path="/confirm-change-password/" element={<PageTitle title="تایید تغییر رمز عبور"><ConfirmChangePassword /></PageTitle>} />
                <Route path="/forget-password/" element={<PageTitle title="فراموشی رمز عبور"><ForgetPassword /></PageTitle>} />
                <Route path="/" element={<PageTitle title="صفحه اصلی"><IndexPage /></PageTitle>} />
                <Route path="/about-us/" element={<PageTitle title="درباره ما"><AboutUsPage /></PageTitle>} />
                <Route path="*" element={<PageTitle title="صفحه یافت نشد"><NotFound /></PageTitle>} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
